class ProductMediaGallery extends HTMLElement {
  constructor() {
    super();
    this.mainSliderElement = this.querySelector('.product-gallery-slider');
    this.thumbSliderElement = this.querySelector('.product-gallery-thumbSlider');
    this.outlineCheck = this.dataset.outline === 'true';
  }

  connectedCallback() {
    const variantId = this.getInitialVariantId();
    this.initSwipers(variantId);
    this.bindEvents();
  }

  getInitialVariantId() {
    const variantScript = this.querySelector('script[type="application/json"]');
    if (variantScript) {
      try {
        const variant = JSON.parse(variantScript.textContent);
        if (variant && variant.id) return variant.id.toString();
      } catch (e) {}
    }
    const fallbackInput = document.querySelector('input[name="id"]');
    return fallbackInput ? fallbackInput.value : null;
  }

  refresh(variantData) {
    if (!variantData || !variantData.id) return;
    const variantId = variantData.id.toString();
    this.renderSlidesForVariant(variantId);
    this.initSwipers(variantId, true);
  }

  renderSlidesForVariant(variantId) {
    const mainWrapper = this.querySelector('.product-gallery-slider .swiper-wrapper');
    const thumbWrapper = this.querySelector('.product-gallery-thumbSlider .swiper-wrapper');
    const mainTemplate = this.querySelector('#Gallery-Master-Main');
    const thumbTemplate = this.querySelector('#Gallery-Master-Thumb');

    if (!mainWrapper || !mainTemplate) return;

    if (this.mainSlider && typeof this.mainSlider.destroy === 'function') {
      this.mainSlider.destroy(true, true);
      this.mainSlider = null;
    }
    if (this.thumbSlider && typeof this.thumbSlider.destroy === 'function') {
      this.thumbSlider.destroy(true, true);
      this.thumbSlider = null;
    }

    mainWrapper.innerHTML = '';
    if (thumbWrapper) thumbWrapper.innerHTML = '';

    const matchesVariant = (slide) => {
      const slideVariantId = slide.dataset.variantId;
      return !slideVariantId || slideVariantId === variantId;
    };

    let isFirstVisible = true;
    mainTemplate.content.querySelectorAll('.swiper-slide').forEach((slide) => {
      if (!matchesVariant(slide)) return;
      const clone = slide.cloneNode(true);
      const img = clone.querySelector('img');
      if (img) {
        img.loading = isFirstVisible ? 'eager' : 'lazy';
        img.setAttribute('fetchpriority', isFirstVisible ? 'high' : 'low');
      }
      clone.setAttribute('aria-hidden', isFirstVisible ? 'false' : 'true');
      mainWrapper.appendChild(clone);
      isFirstVisible = false;
    });

    if (thumbWrapper && thumbTemplate) {
      thumbTemplate.content.querySelectorAll('.swiper-slide').forEach((slide) => {
        if (matchesVariant(slide)) thumbWrapper.appendChild(slide.cloneNode(true));
      });
    }
  }

  getInitialSlideIndex(variantId) {
    const slides = Array.from(this.mainSliderElement.querySelectorAll('.swiper-slide'));
    let targetIndex = slides.findIndex((slide) => slide.dataset.variantId === variantId);

    if (targetIndex === -1) {
      const variantJsonScript = document.querySelector(
        'variant-selects [type="application/json"], variant-radios [type="application/json"]'
      );
      if (variantJsonScript) {
        try {
          const variantDataArray = JSON.parse(variantJsonScript.textContent);
          const matchingVariant = variantDataArray.find((v) => v.id.toString() === variantId);
          if (matchingVariant && matchingVariant.featured_media) {
            targetIndex = slides.findIndex(
              (slide) => slide.dataset.mediaId == matchingVariant.featured_media.id
            );
          }
        } catch (e) {}
      }
    }

    return targetIndex !== -1 ? targetIndex : 0;
  }

  initSwipers(variantId, isRefresh = false) {
    if (isRefresh) this.renderSlidesForVariant(variantId);

    const nextBtn = this.querySelector('.swiper-nav-button--next');
    const prevBtn = this.querySelector('.swiper-nav-button--prev');
    const paginationEl = this.querySelector('.swiper-pagination');

    if (!this.mainSliderElement) return;

    const initialIndex = this.getInitialSlideIndex(variantId);

    if (this.thumbSliderElement) {
      this.thumbSlider = new Swiper(this.thumbSliderElement, {
        initialSlide: initialIndex,
        slidesPerView: 'auto',
        grabCursor: true,
        spaceBetween: 0,
        freeMode: true,
        watchSlidesProgress: true,
        slideToClickedSlide: true,
        mousewheel: {
          forceToAxis: true,
          sensitivity: 1,
          releaseOnEdges: true,
        }
      });
    }

    this.mainSlider = new Swiper(this.mainSliderElement, {
      initialSlide: initialIndex,
      speed: 400,
      grabCursor: true,
      slidesPerView: 1.06,
      spaceBetween: 0,
      keyboard: {
        enabled: false,
        onlyInViewport: true,
        pageUpDown: true,
      },
      pagination: {
        el: paginationEl,
        clickable: true,
      },
      thumbs: {
        swiper: this.thumbSlider,
      },
      mousewheel: {
        forceToAxis: true,
      },
      breakpoints: {
        992: {
          slidesPerView: 1,
          spaceBetween: 0,
          navigation: {
            nextEl: nextBtn,
            prevEl: prevBtn,
          }
        }
      },
      on: {
        init: (swiper) => {
          swiper.slides.forEach((slide, index) => {
            slide.setAttribute('aria-hidden', index === swiper.activeIndex ? 'false' : 'true');
          });
        },
        slideChangeTransitionStart: () => {
          this.pauseAllVideos();
        },
        activeIndexChange: (swiper) => {
          const currentSlideVideo = swiper.slides[swiper.activeIndex]?.querySelector('video');
          const previousSlideVideo = swiper.slides[swiper.previousIndex]?.querySelector('video');

          if (previousSlideVideo) previousSlideVideo.pause();
          if (currentSlideVideo) currentSlideVideo.play();

          swiper.slides.forEach((slide, index) => {
            slide.setAttribute('aria-hidden', index === swiper.activeIndex ? 'false' : 'true');
          });
        }
      }
    });
  }

  bindEvents() {
    if (this._eventsBound) return;
    this._eventsBound = true;

    this.mainSliderElement.setAttribute('tabindex', '0');
    this.mainSliderElement.setAttribute('aria-label', 'Product Image Gallery');

    this.mainSliderElement.addEventListener('focusin', () => {
      if (this.mainSlider && this.mainSlider.keyboard) {
        this.mainSlider.keyboard.enable();
      }
    });

    this.mainSliderElement.addEventListener('focusout', () => {
      if (this.mainSlider && this.mainSlider.keyboard) {
        this.mainSlider.keyboard.disable();
      }
    });
  }

  pauseAllVideos() {
    const playingVideos = this.querySelectorAll('video-modal[active="true"], iframe');
    playingVideos.forEach((video) => {
      if (video.pauseVideo) video.pauseVideo();
    });

    const inlineVideos = this.querySelectorAll('.video-slide video');
    inlineVideos.forEach((video) => {
      if (!video.paused) video.pause();
    });
  }
}

customElements.define('product-media-gallery', ProductMediaGallery);

class AccordionItem extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.button = this.querySelector('.accordion-header');
    if (this.button) {
      this.button.addEventListener('click', this.toggle.bind(this));
    }
  }

  toggle() {
    const isOpen = this.hasAttribute('open');
    
    if (!isOpen) {
      const container = this.closest('.product-accordions-container') || document;
      const allAccordions = container.querySelectorAll('accordion-item');
      
      allAccordions.forEach((accordion) => {
        if (accordion !== this && accordion.hasAttribute('open')) {
          accordion.removeAttribute('open');
          const btn = accordion.querySelector('.accordion-header');
          if (btn) btn.setAttribute('aria-expanded', 'false');
        }
      });

      this.setAttribute('open', '');
      this.button.setAttribute('aria-expanded', 'true');
    } else {
      this.removeAttribute('open');
      this.button.setAttribute('aria-expanded', 'false');
    }
  }
}

customElements.define('accordion-item', AccordionItem);

class ContentReveal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.button = this.querySelector('.reveal-btn');
    this.content = this.querySelector('.reveal-content');
    
    if (!this.button || !this.content) return;

    this.textMore = this.button.dataset.textMore || 'VIEW FULL LIST';
    this.textLess = this.button.dataset.textLess || 'CLOSE FULL LIST';

    this.button.addEventListener('click', this.toggle.bind(this));
  }

  toggle() {
    const isHidden = this.content.style.display === 'none';
    
    if (isHidden) {
      this.content.style.display = 'block';
      this.button.textContent = this.textLess;
    } else {
      this.content.style.display = 'none';
      this.button.textContent = this.textMore;
    }
  }
}

customElements.define('content-reveal', ContentReveal);

class ResultsTabs extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.tabs = this.querySelectorAll('.results-tab-btn');
    this.panels = this.querySelectorAll('.results-panel');

    if (!this.tabs.length || !this.panels.length) return;

    this.tabs.forEach(tab => {
      tab.addEventListener('click', () => this.switchTab(tab));
    });
  }

  switchTab(selectedTab) {
    this.tabs.forEach(tab => {
      tab.classList.remove('is-active');
      tab.setAttribute('aria-selected', 'false');
    });
    this.panels.forEach(panel => {
      panel.classList.remove('is-active');
      panel.setAttribute('hidden', '');
    });

    selectedTab.classList.add('is-active');
    selectedTab.setAttribute('aria-selected', 'true');

    const index = selectedTab.dataset.index;
    const targetPanel = this.querySelector(`#ResultPanel-${this.dataset.sectionId || ''}-${index}`.replace(/--/g, '-'));
    
    const exactPanel = this.panels[parseInt(index) - 1];
    
    if (exactPanel) {
      exactPanel.classList.add('is-active');
      exactPanel.removeAttribute('hidden');
    }
  }
}

customElements.define('results-tabs', ResultsTabs);